import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import Breadcrums from "../../common/breadcrumbs";
import { SWAL_SETTINGS, INPUT_LENGTH_40, EXPERTISE, languageSpoken, GENDER, ASTROLOGY_SYSTEMS, EMAIL_REGEX } from "../../../../utils/Constants";
import {
  handleServerValidations,
  DT,
  globalLoader,
  capitalizeFirstLetterOfEachWord,
} from "../../../../utils/commonfunction";
import * as Yup from "yup";
import CustomError from "../../../../utils/customError";
import { useTranslation } from "react-i18next";
import { Add } from "../../services/aiastrologer.services";
import { List as ListCategories } from "../../services/pujaCategory.services";
import { TagPicker, Tooltip, Whisper, DatePicker } from "rsuite";
import PhoneInput from "../../../../utils/PhoneInput";
import { COMMON_INPUT_VALIDATION, PHONE_VALIDATION } from "../../../../utils/commonValidations";

const languageData = languageSpoken.map(lang => ({ label: lang.name, value: lang.code }));
const systemData = ASTROLOGY_SYSTEMS.map(sys => ({ label: sys, value: sys }));

const AstrologerAdd = (props) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [previewimage, setPreviewImage] = useState("");
  const [saveType, setSaveType] = useState("");
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    ListCategories({ per_page: 100 })
      .then((res) => {
        if (res.data && res.data.list) {
          setCategories(res.data.list);
        }
      })
      .catch(err => console.log("Category error:", err));
  }, []);

  const breadcrumbs = [
    { title: t("link_dashboard"), url: "/admin/dashboard" },
    {
      title: t("AI Astrologer"),
      url: "/admin/astrologer-management/ai-astrologer/list/1",
    },
    { title: t("Add"), url: "" }, // Changed "Edit" to "Add"
  ];

  const validationSchema = Yup.object().shape({
    name: Yup.string()
      .trim()
      .max(INPUT_LENGTH_40, DT(t("max_length_error_dynamic"), [INPUT_LENGTH_40]))
      .concat(COMMON_INPUT_VALIDATION)
      .required("Name is required"),
    screen_name: Yup.string()
      .trim()
      .max(INPUT_LENGTH_40, DT(t("max_length_error_dynamic"), [INPUT_LENGTH_40])),
    // AI astrologers never log in, so an email is optional for them.
    email: Yup.string()
      .trim()
      .test("optional-email", "Enter a valid email address", (value) => !value || EMAIL_REGEX.test(value)),
    phone_number: Yup.string()
      .required("Phone number is required")
      .matches(/^[6-9]\d{9}$/, "Phone number must be a valid 10-digit Indian number"),
    expertise: Yup.array()
      .min(1, "At least one expertise is required")
      .of(
        Yup.object({
          puja_category_id: Yup.number().required("Expertise type is required"),
        })
      ),
    language_spoken: Yup.array()
      .min(1, "At least one language must be provided")
      .required("Language spoken is required"),
    preferred_language: Yup.string().optional(),
    experience: Yup.number()
      .typeError("Experience must be a number")
      .min(0, "Experience cannot be negative")
      .max(100, "Experience seems too high can not exceed 100")
      .required("Experience is required"),
    education: Yup.string()
      .trim()
      .max(100, "Education cannot exceed 100 characters")
      .required("Education is required"),
    gender: Yup.string().required("Gender is required"),
    date_of_birth: Yup.date()
      .required("Date of birth is required")
      .max(new Date(), "Date of birth cannot be in the future"),
    promotional_video: Yup.mixed()
      .nullable()
      .test("fileSize", "Video size is too large (max 50MB)", (value) => !value || (value && value.size <= 50 * 1024 * 1024))
      .test("fileType", "Unsupported video format", (value) => !value || (value && value.type.startsWith("video/"))),
    gov_id_proof: Yup.mixed()
      .required("Government ID proof is required")
      .test("fileSize", "File size is too large (max 5MB)", (value) => value && value.size <= 5 * 1024 * 1024),
    astrology_certifications: Yup.mixed()
      .required("Astrology certifications are required")
      .test("fileSize", "File size is too large (max 5MB)", (value) => value && value.size <= 5 * 1024 * 1024),
    cancelled_cheque: Yup.mixed()
      .required("Cancelled bank cheque is required")
      .test("fileSize", "File size is too large (max 5MB)", (value) => value && value.size <= 5 * 1024 * 1024),
    profile_image: Yup.mixed()
      .required("Image is required")
      .test("fileSize", "Image size is too large (max 5MB)", (value) => value && value.size <= 5 * 1024 * 1024)
      .test("fileType", "Unsupported file format", (value) => value && ["image/jpg", "image/jpeg", "image/png", "image/webp", "image/gif"].includes(value.type)),
    ai_astrologer_id: Yup.string()
      .trim()
      .required("Astrologer ID is required"),
    about_me: Yup.string()
      .trim()
      .required("About me is required"),
  });

  const formik = useFormik({
    initialValues: {
      name: "",
      screen_name: "",
      email: "",
      phone_number: "",
      selected_expertise: [], // For TagPicker state
      expertise: [], // For submission data
      language_spoken: [],
      preferred_language: "",
      experience: "",
      education: "",
      gender: "",
      date_of_birth: null,
      promotional_video: null,
      gov_id_proof: null,
      astrology_certifications: null,
      cancelled_cheque: null,
      profile_image: null,
      ai_astrologer_id: "",
      about_me: "",
      sort_order: 0,
      is_subscription_astrologer: "false",
      system: [],
      chart_type: "",
      service_type: "both",
    },
    validationSchema: validationSchema,
    onSubmit: (values, { setSubmitting }) => {
      globalLoader(true);
      const formData = new FormData();
      const formattedName = values.name ? capitalizeFirstLetterOfEachWord(values.name) : values.name;
      const formattedScreenName = values.screen_name ? capitalizeFirstLetterOfEachWord(values.screen_name) : values.screen_name;
      formData.append("name", formattedName);
      formData.append("screen_name", formattedScreenName);
      formData.append("email", values.email);
      formData.append("phone_number", values.phone_number);
      formData.append("expertise", JSON.stringify(values.expertise));
      formData.append("experience", values.experience);
      formData.append("education", values.education);
      formData.append("gender", values.gender);
      formData.append("date_of_birth", values.date_of_birth ? new Date(values.date_of_birth).toISOString().split('T')[0] : "");
      formData.append("language_spoken", JSON.stringify(values.language_spoken));
      formData.append("preferred_language", values.preferred_language);
      formData.append("ai_astrologer_id", values.ai_astrologer_id);
      formData.append("about_me", values.about_me);
      formData.append("sort_order", values.sort_order);
      formData.append("is_subscription_astrologer", values.is_subscription_astrologer);
      formData.append("system", JSON.stringify(values.system));
      formData.append("chart_type", values.chart_type);
      formData.append("service_type", values.service_type);
      if (values.profile_image) {
        formData.append("profile_image", values.profile_image);
      }
      if (values.promotional_video) {
        formData.append("promotional_video", values.promotional_video);
      }
      if (values.gov_id_proof) {
        formData.append("gov_id_proof", values.gov_id_proof);
      }
      if (values.astrology_certifications) {
        formData.append("astrology_certifications", values.astrology_certifications);
      }
      if (values.cancelled_cheque) {
        formData.append("cancelled_cheque", values.cancelled_cheque);
      }

      Add(formData)
        .then((response) => {
          setSubmitting(false);
          globalLoader(false);
          if (response.success) {
            Swal.fire({
              icon: "success",
              text: response.message,
              ...SWAL_SETTINGS,
            });
            if (saveType === "Save") {
              navigate(`/admin/astrologer-management/ai-astrologer/list/1`);
            } else {
              formik.resetForm({ values: { ...formik.initialValues, language_spoken: values.language_spoken, experience: values.experience, profile_image: null } });
              setPreviewImage("");
            }
          } else {
            Swal.fire({
              icon: "error",
              text: handleServerValidations(response),
              ...SWAL_SETTINGS,
            });
          }
        })
        .catch((error) => {
          globalLoader(false);
          Swal.fire({
            icon: "error",
            text: handleServerValidations(error),
            ...SWAL_SETTINGS,
          });
        });
    },
  });

  // Sync TagPicker selection with the expertise array
  useEffect(() => {
    const updatedExpertise = formik.values.selected_expertise.map(expertValue => {
      return { puja_category_id: expertValue, price_per_minute: 0 };
    });
    formik.setFieldValue('expertise', updatedExpertise);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values.selected_expertise]);


  console.log("Formik Values:", formik.values); // Debugging line to check form values
  console.log("errors:", formik.errors); // Debugging line to check form errors

  return (
    <>
      <Breadcrums data={breadcrumbs} />
      <form onSubmit={formik.handleSubmit}>
        <div className="row row-sm">
          <div className="col-lg-12 col-md-12 animation_fade">
            <div className="card custom-card">
              <div className="card-body">
                <div>
                  <h6 className="main-content-label mb-3">
                    {t("Add AI Astrologer")}
                  </h6>
                </div>
                <div className="row row-sm">
                  {/* Row 1: Profile Image */}
                  <div className="col-lg-6 form-group">
                    <label htmlFor="profile_image" className="text-left d-flex">
                      {t("Profile Image")}:<span className="requirestar">*</span>
                      <Whisper placement="top" trigger="hover" speaker={<Tooltip>{t("image_support_tooltip")}</Tooltip>}>
                        <span className="field-more-info mt-1 ms-1 cp">?</span>
                      </Whisper>
                    </label>
                    <input
                      className="form-control imgInput"
                      id="profile_image"
                      name="profile_image"
                      accept="image/*"
                      type="file"
                      onChange={(event) => {
                        const file = event.currentTarget.files[0];
                        formik.setFieldValue("profile_image", file);
                        setPreviewImage(file ? URL.createObjectURL(file) : "");
                      }}
                    />
                    <CustomError name="profile_image" form={formik} />
                    {previewimage && (
                      <div className="mt-2"><img src={previewimage} style={{ height: "100px", borderRadius: "8px" }} alt="Profile Preview" /></div>
                    )}
                  </div>

                  {/* Row 2: Name and Gender */}
                  <div className="col-md-6 form-group">
                    <label htmlFor="name" className="text-left d-flex">
                      {t("Name")}:<span className="requirestar">*</span>
                    </label>
                    <input
                      name="name"
                      id="name"
                      type="text"
                      onChange={formik.handleChange}
                      onBlur={(e) => {
                        formik.handleBlur(e);
                        if (e.target.value) {
                          formik.setFieldValue("name", capitalizeFirstLetterOfEachWord(e.target.value));
                        }
                      }}
                      value={formik.values.name}
                      className="form-control"
                      placeholder={t("Enter name")}
                    />
                    <CustomError name="name" form={formik} />
                  </div>

                  <div className="col-md-6 form-group">
                    <label htmlFor="screen_name" className="text-left d-flex">
                      {t("Screen Name")}:
                    </label>
                    <input
                      name="screen_name"
                      id="screen_name"
                      type="text"
                      onChange={formik.handleChange}
                      onBlur={(e) => {
                        formik.handleBlur(e);
                        if (e.target.value) {
                          formik.setFieldValue("screen_name", capitalizeFirstLetterOfEachWord(e.target.value));
                        }
                      }}
                      value={formik.values.screen_name}
                      className="form-control"
                      placeholder={t("Enter screen name")}
                    />
                    <CustomError name="screen_name" form={formik} />
                  </div>

                  {/* Sort Order */}
                  <div className="col-md-6 form-group">
                    <label htmlFor="sort_order" className="text-left d-flex">
                      Sort Order / Priority :
                    </label>
                    <input
                      name="sort_order"
                      id="sort_order"
                      type="number"
                      min="0"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.sort_order}
                      className="form-control"
                      placeholder="Enter sort order (e.g. 1, 2, 3)"
                    />
                  </div>

                  <div className="col-md-6 form-group">
                    <label htmlFor="is_subscription_astrologer" className="text-left d-flex">
                      {t("Is Subscription Astrologer")}:
                    </label>
                    <select
                      name="is_subscription_astrologer"
                      id="is_subscription_astrologer"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.is_subscription_astrologer}
                      className="form-control"
                    >
                      <option value="false">{t("No")}</option>
                      <option value="true">{t("Yes")}</option>
                    </select>
                    <CustomError name="is_subscription_astrologer" form={formik} />
                  </div>

                  <div className="col-md-6 form-group">
                    <label htmlFor="system" className="text-left d-flex">
                      {t("System")}:
                    </label>
                    <TagPicker
                      data={systemData}
                      block
                      value={formik.values.system}
                      onChange={(value) => formik.setFieldValue("system", value || [])}
                      placeholder={t("Select System")}
                    />
                    <CustomError name="system" form={formik} />
                  </div>

                  <div className="col-md-6 form-group">
                    <label htmlFor="chart_type" className="text-left d-flex">
                      {t("Chart")}:
                    </label>
                    <select
                      name="chart_type"
                      id="chart_type"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.chart_type}
                      className="form-control"
                    >
                      <option value="">{t("Select Chart")}</option>
                      <option value="SINGLE">{t("SINGLE")}</option>
                      <option value="DUAL">{t("DUAL")}</option>
                    </select>
                    <CustomError name="chart_type" form={formik} />
                  </div>

                  <div className="col-md-6 form-group">
                    <label htmlFor="service_type" className="text-left d-flex">
                      {t("Service Provided")}:<span className="requirestar">*</span>
                    </label>
                    <select
                      name="service_type"
                      id="service_type"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.service_type}
                      className="form-control"
                    >
                      <option value="both">{t("Both (Chat & Call)")}</option>
                      <option value="chat">{t("Only Chat")}</option>
                      <option value="call">{t("Only Call")}</option>
                    </select>
                    <CustomError name="service_type" form={formik} />
                  </div>

                  <div className="col-md-6 form-group">
                    <label htmlFor="ai_astrologer_id" className="text-left d-flex">
                      {t("Astrologer ID")}:<span className="requirestar">*</span>
                    </label>
                    <input
                      name="ai_astrologer_id"
                      id="ai_astrologer_id"
                      type="text"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.ai_astrologer_id}
                      className="form-control"
                      placeholder={t("Enter Astrologer ID")}
                    />
                    <CustomError name="ai_astrologer_id" form={formik} />
                  </div>

                  <div className="col-md-6 form-group">
                    <label htmlFor="about_me" className="text-left d-flex">
                      {t("About Me")}:<span className="requirestar">*</span>
                    </label>
                    <textarea
                      name="about_me"
                      id="about_me"
                      rows="2"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.about_me}
                      className="form-control"
                      placeholder={t("Enter about me details")}
                    />
                    <CustomError name="about_me" form={formik} />
                  </div>

                  <div className="col-md-6 form-group">
                    <label htmlFor="gender" className="text-left d-flex">
                      {t("Gender")}:<span className="requirestar">*</span>
                    </label>
                    <select
                      name="gender"
                      id="gender"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.gender}
                      className="form-control"
                    >
                      <option value="">Select Gender</option>
                      {GENDER.map((gender, i) => (
                        <option key={i} value={gender}>{t(gender)}</option>
                      ))}
                    </select>
                    <CustomError name="gender" form={formik} />
                  </div>

                  {/* Row 3: DOB and Email */}
                  <div className="col-md-6 form-group">
                    <label htmlFor="date_of_birth" className="text-left d-flex">
                      {t("Date of Birth")}:<span className="requirestar">*</span>
                    </label>
                    <DatePicker
                      oneTap
                      block
                      format="dd/MM/yyyy"
                      value={formik.values.date_of_birth}
                      onChange={(value) => formik.setFieldValue("date_of_birth", value)}
                      onBlur={() => formik.setFieldTouched("date_of_birth", true)}
                      placeholder="Select Date of Birth"
                      shouldDisableDate={(date) => date > new Date()}
                    />
                    <CustomError name="date_of_birth" form={formik} />
                  </div>

                  <div className="col-md-6 form-group">
                    <label htmlFor="email" className="text-left d-flex">
                      {t("label_email")}:
                    </label>
                    <input
                      name="email"
                      id="email"
                      type="email"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.email}
                      className="form-control"
                      placeholder={t("placeholder_email")}
                    />
                    <CustomError name="email" form={formik} />
                  </div>

                  {/* Row 4: Phone and Promo Video */}
                  <div className="col-md-6 form-group">
                    <label className="text-left d-flex" htmlFor="phone_number">
                      {t("label_phone_number")}:<span className="requirestar">*</span>
                    </label>
                    <input
                      name="phone_number"
                      id="phone_number"
                      type="text"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.phone_number}
                      className="form-control"
                      placeholder={t("Enter phone number")}
                    />
                    <CustomError name="phone_number" form={formik} />
                  </div>

                  <div className="col-lg-6 form-group">
                    <label htmlFor="promotional_video" className="text-left d-flex">
                      {t("Promotional Video")}:
                    </label>
                    <input
                      className="form-control"
                      id="promotional_video"
                      name="promotional_video"
                      accept="video/*"
                      type="file"
                      onChange={(event) => {
                        formik.setFieldValue("promotional_video", event.currentTarget.files[0]);
                      }}
                    />
                    <CustomError name="promotional_video" form={formik} />
                  </div>

                  {/* Row 5: Languages and Preferred Language */}
                  <div className="col-md-6 form-group">
                    <label htmlFor="language_spoken" className="text-left d-flex">
                      {t("Language Spoken")}:<span className="requirestar">*</span>
                    </label>
                    <TagPicker
                      data={languageData}
                      block
                      value={formik.values.language_spoken}
                      onChange={(value) => formik.setFieldValue('language_spoken', value || [])}
                      placeholder="Select one or more languages"
                    />
                    <CustomError name="language_spoken" form={formik} />
                  </div>

                  <div className="col-md-6 form-group">
                    <label htmlFor="preferred_language" className="text-left d-flex">
                      {t("Preferred Language")}:
                    </label>
                    <select
                      name="preferred_language"
                      id="preferred_language"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.preferred_language}
                      className="form-control"
                    >
                      <option value="">{t("Select Preferred Language")}</option>
                      {languageSpoken.map((lang, i) => (
                        <option key={i} value={lang.code}>
                          {lang.name}
                        </option>
                      ))}
                    </select>
                    <CustomError name="preferred_language" form={formik} />
                  </div>

                  <div className="col-md-6 form-group">
                    <label htmlFor="experience" className="text-left d-flex">
                      {t("Experience (in Years)")}:<span className="requirestar">*</span>
                    </label>
                    <input
                      name="experience"
                      id="experience"
                      type="number"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.experience}
                      className="form-control"
                      placeholder={t("Enter years of experience")}
                    />
                    <CustomError name="experience" form={formik} />
                  </div>

                  <div className="col-md-6 form-group">
                    <label htmlFor="education" className="text-left d-flex">
                      {t("Education")}:<span className="requirestar">*</span>
                    </label>
                    <input
                      name="education"
                      id="education"
                      type="text"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.education}
                      className="form-control"
                      placeholder={t("Enter education (e.g. B.A. in Astrology)")}
                    />
                    <CustomError name="education" form={formik} />
                  </div>

                  {/* Row 6: Expertise */}
                  <div className="col-md-12">
                    <hr />
                    <h6>{t("Expertise")}</h6>
                  </div>

                  <div className="col-md-6 form-group">
                    <label htmlFor="expertise" className="text-left d-flex">
                      {t("Select Expertise")}:<span className="requirestar">*</span>
                    </label>
                    <TagPicker
                      data={categories.map(cat => ({ label: cat.title, value: cat.id }))}
                      block
                      value={formik.values.selected_expertise}
                      onChange={(value) => formik.setFieldValue('selected_expertise', value || [])}
                      onBlur={formik.handleBlur}
                      name="selected_expertise"
                      placeholder="Select one or more expertise"
                    />
                    {formik.touched.selected_expertise && typeof formik.errors.expertise === 'string' ? (
                      <div className="text-danger d-flex text-left">{formik.errors.expertise}</div>
                    ) : null}
                  </div>

                  {/* Row 7: KYC Verification */}
                  <div className="col-md-12">
                    <hr />
                    <h6>{t("KYC Verification")}</h6>
                  </div>

                  <div className="col-lg-4 form-group">
                    <label htmlFor="gov_id_proof" className="text-left d-flex">
                      {t("Government ID Proof")}:<span className="requirestar">*</span>
                    </label>
                    <input
                      className="form-control"
                      id="gov_id_proof"
                      name="gov_id_proof"
                      type="file"
                      onChange={(event) => {
                        formik.setFieldValue("gov_id_proof", event.currentTarget.files[0]);
                      }}
                    />
                    <CustomError name="gov_id_proof" form={formik} />
                  </div>

                  <div className="col-lg-4 form-group">
                    <label htmlFor="astrology_certifications" className="text-left d-flex">
                      {t("Astrology Certifications")}:<span className="requirestar">*</span>
                    </label>
                    <input
                      className="form-control"
                      id="astrology_certifications"
                      name="astrology_certifications"
                      type="file"
                      onChange={(event) => {
                        formik.setFieldValue("astrology_certifications", event.currentTarget.files[0]);
                      }}
                    />
                    <CustomError name="astrology_certifications" form={formik} />
                  </div>

                  <div className="col-lg-4 form-group">
                    <label htmlFor="cancelled_cheque" className="text-left d-flex">
                      {t("Cancelled Bank Cheque")}:<span className="requirestar">*</span>
                    </label>
                    <input
                      className="form-control"
                      id="cancelled_cheque"
                      name="cancelled_cheque"
                      type="file"
                      onChange={(event) => {
                        formik.setFieldValue("cancelled_cheque", event.currentTarget.files[0]);
                      }}
                    />
                    <CustomError name="cancelled_cheque" form={formik} />
                  </div>
                </div>

                <div className="col-12 mt-4">
                  <button onClick={() => setSaveType("Save")} className="btn btn-success mr-2" type="submit" disabled={formik.isSubmitting}>
                    {t("btn_submit")}
                  </button>
                  <button className="btn btn-secondary" type="button" onClick={() => navigate(-1)}>
                    {t("btn_cancel")}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </form>
    </>
  );
};

export default AstrologerAdd;


